import React from 'react';
import { useNavigate } from 'react-router-dom';
import HomeNavbar from './HomeNavbar';
import './LandingPage.css';

function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="landing-page">
      <HomeNavbar />
      <div className="landing-hero pt-32">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-content">
            <h1 className="hero-title">
              Revenue Validation and Qualification Platform
            </h1>
            <p className="hero-subtitle">
              ConvrsAI by Market Wavegen ensures only real, verified buying intent reaches your sales team.
            </p>
            <p className="hero-description">
              Most demand systems detect interest. ConvrsAI by Market Wavegen proves buyer reality before sales ever engages.
            </p>
            <div className="hero-cta">
              <a
                href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ1LRerQzILalOQbfT9l5lWQ6abwrkk6n45IbqR_cPsf8tH09a8kVlButSqrKMTkMo1De-h8vUdB"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary-large"
              >
                Book a Demo
              </a>
              <button 
                className="btn-secondary-large" 
                onClick={() => navigate('/login')}
              >
                Sign In
              </button>
            </div>
          </div>
        </section>

        {/* Problem Section */}
        <section className="problem-section">
          <div className="section-container">
            <h2 className="section-title">The Problem: Activity Is Not Revenue Reality</h2>
            <p className="section-subtitle">Activity Creates Pipeline. Validation Creates Revenue.</p>
            <div className="problem-content">
              <p className="problem-text">
                Modern revenue teams are drowning in leads but starving for certainty. Forms get completed. Content gets downloaded. Signals get triggered. Yet sales conversations begin with friction:
              </p>
              <ul className="problem-list">
                <li>"I don't remember filling this out."</li>
                <li>Wrong phone numbers</li>
                <li>Students posing as buyers</li>
                <li>Vendors downloading competitor assets</li>
                <li>Junior employees entering enterprise funnels</li>
              </ul>
              <p className="problem-highlight">
                Pipeline looks healthy until it meets reality. Every unvalidated lead introduces risk: Wasted SDR capacity, Slower deal cycles, Forecast volatility, Rising acquisition costs, Sales and marketing misalignment.
              </p>
              <p className="problem-conclusion">
                <strong>The problem isn't lead generation. The problem is lead trustworthiness.</strong> ConvrsAI by Market Wavegen fixes this before revenue is exposed.
              </p>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="solution-section">
          <div className="section-container">
            <h2 className="section-title">What ConvrsAI by Market Wavegen Does</h2>
            <p className="solution-intro">
              ConvrsAI by Market Wavegen is a Revenue Validation Platform and Qualification Engine designed to ensure every lead entering your CRM represents a real buying opportunity. It evaluates each lead as a risk decision not just a record.
            </p>
            <p className="solution-intro">
              ConvrsAI by Market Wavegen validates: <strong>The business, The buyer, The intent, The readiness</strong> — Before sales touches the lead.
            </p>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="how-it-works-section">
          <div className="section-container">
            <h2 className="section-title">How ConvrsAI by Market Wavegen Works</h2>
            <p className="section-subtitle">System-Level View</p>
            <p className="how-it-works-intro">
              ConvrsAI by Market Wavegen ingests leads from any source and applies multiple validation and qualification layers:
            </p>
            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon">🔍</div>
                <h3>Deep Web Lead Validation</h3>
                <p className="feature-description">What It Does</p>
                <p>ConvrsAI by Market Wavegen validates each lead using deep web search and multiple data sources not just a single provider. It verifies: Company legitimacy, Website presence and activity, Industry classification, Geographic relevance.</p>
                <p className="feature-why">Why It Matters</p>
                <p>Most bad leads look good on the surface. Deep web validation removes: Fake companies, Dormant businesses, Mismatched industries. Sales only sees real, active businesses.</p>
              </div>

              <div className="feature-card">
                <div className="feature-icon"></div>
                <h3>Firmographic Verification</h3>
                <p className="feature-description">What It Does</p>
                <p>ConvrsAI by Market Wavegen cross-checks and normalizes: Company size, Revenue band, Employee count, Growth indicators. Each lead is matched against your ICP rules.</p>
                <p className="feature-why">Why It Matters</p>
                <p>Revenue leakage happens when: SMBs enter enterprise funnels, Budget-fit is assumed, Sales spends cycles disqualifying. ConvrsAI by Market Wavegen filters misaligned accounts before they reach sales.</p>
              </div>

              <div className="feature-card">
                <div className="feature-icon"></div>
                <h3>Job Role and Title Validation</h3>
                <p className="feature-description">What It Does</p>
                <p>ConvrsAI by Market Wavegen validates: Job title accuracy, Role seniority, Decision influence, Department relevance. It removes inflated, misleading, or irrelevant titles.</p>
                <p className="feature-why">Why It Matters</p>
                <p>Titles lie. Roles do not. This prevents: Non-decision makers reaching SDRs, Junior roles entering ABM motions, Late-stage deal blockers. Sales starts with the right buyer, every time.</p>
              </div>

              <div className="feature-card">
                <div className="feature-icon"></div>
                <h3>AI-Led Email Qualification</h3>
                <p className="feature-description">What It Does</p>
                <p>ConvrsAI by Market Wavegen runs automated, intent-focused email conversations that: Ask qualification-driven questions, Detect readiness signals, Adapt follow-ups dynamically, Capture explicit buying cues.</p>
                <p className="feature-why">Why It Matters</p>
                <p>Email opens and clicks do not equal intent. AI-led dialogue: Clarifies interest, Identifies urgency, Surfaces real buying windows. Marketing learns before sales calls.</p>
              </div>

              <div className="feature-card">
                <div className="feature-icon"></div>
                <h3>AI-Powered Voice Qualification</h3>
                <p className="feature-description">What It Does</p>
                <p>ConvrsAI by Market Wavegen places short, AI-led voice calls to: Confirm interest, Validate urgency, Capture objections, Identify next steps. Calls are structured, consistent, and context aware.</p>
                <p className="feature-why">Why It Matters</p>
                <p>Voice reveals truth faster than forms. This removes: SDR discovery calls, First-call disqualification, "I don't remember filling the form" conversations. Sales receives clarity, not confusion.</p>
              </div>

              <div className="feature-card">
                <div className="feature-icon"></div>
                <h3>Conversation Intelligence</h3>
                <p className="feature-description">What It Does</p>
                <p>ConvrsAI by Market Wavegen converts every interaction into: Intent summaries, Objection signals, Timing indicators, Sales-ready context. All attached directly to the lead.</p>
                <p className="feature-why">Why It Matters</p>
                <p>Sales doesn't need to rediscover context. They enter conversations informed. Deals move faster.</p>
              </div>

              <div className="feature-card">
                <div className="feature-icon"></div>
                <h3>Lead Confidence Scoring</h3>
                <p className="feature-description">What It Does</p>
                <p>Each lead receives a confidence score based on: Validation depth, Firmographic fit, Role relevance, Conversation outcomes, Confirmed intent signals.</p>
                <p className="feature-why">Why It Matters</p>
                <p>Sales prioritizes certainty over volume. Forecasting improves. Pipeline becomes predictable.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Outcome Section */}
        <section className="outcome-section">
          <div className="section-container">
            <h2 className="section-title">The Outcome: No More Wasted Leads</h2>
            <p className="outcome-intro">With ConvrsAI by Market Wavegen:</p>
            <ul className="outcome-list">
              <li>No more wrong phone numbers</li>
              <li>No more "I never filled this form"</li>
              <li>No more irrelevant titles in sales queues</li>
              <li>No more SDR time wasted on disqualification</li>
              <li>No more pipelines built on assumptions</li>
            </ul>
            <p className="outcome-conclusion">
              Only validated, qualified, sales-ready demand reaches your team.
            </p>
          </div>
        </section>

        {/* Built For Section */}
        <section className="built-for-section">
          <div className="section-container">
            <h2 className="section-title">Built for Modern B2B Revenue Teams</h2>
            <p className="built-for-text">
              ConvrsAI by Market Wavegen works across: All B2B industries, Any lead source or campaign, Any CRM or sales workflow. If your business depends on pipeline quality, ConvrsAI by Market Wavegen becomes a critical control layer in your revenue system.
            </p>
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section-final">
          <div className="section-container">
            <h2 className="cta-title">Turn Demand into Revenue Reality</h2>
            <p className="cta-subtitle">Stop sending uncertainty to sales. Start sending validated buying intent.</p>
            <div className="cta-button-wrapper">
              <a
                href="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ1LRerQzILalOQbfT9l5lWQ6abwrkk6n45IbqR_cPsf8tH09a8kVlButSqrKMTkMo1De-h8vUdB"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary-large"
              >
                Book a Demo
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="footer-section">
          <div className="footer-content">
            <p className="footer-text">
              © 2026 ConvrsAI by Market Wavegen. All rights reserved.
            </p>
            <nav aria-label="Legal links" className="footer-links">
              <a href="/terms" className="footer-link">Terms and Conditions</a>
              <span className="footer-sep"> • </span>
              <a href="/privacy-policy" className="footer-link">Privacy Policy</a>
            </nav>
            <p className="footer-subtext">
              Built for Modern B2B Revenue Teams
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default LandingPage;
